import i18next from 'i18next'
import {Moment} from 'moment-timezone'
import {injectable} from 'inversify'
import {makeAutoObservable} from 'mobx'
import 'reflect-metadata'

import {TRangePickerContainerStore} from '@/components/containers/RangePickerContainer/TRangePickerContainerStore'
import {getDates, getPeriod, getPrevDates} from '@/stores'
import {getDefaultRangePresets} from '@/constants/dateRangePresets'
import {TRangePickerStoreOptions, PeriodType} from './models'

@injectable()
export class RangePickerStore implements TRangePickerContainerStore {
    constructor(options: TRangePickerStoreOptions = {}) {
        makeAutoObservable(this)

        if (options.period) {
            this.setPeriod(options.period)
        }

        if (options.rangePresets) {
            this._rangePresets = options.rangePresets
        }
    }

    public disabled = false
    public withLeftMenu = true
    public periodTitle = i18next.t('RangePicker.calendarTitle')
    public endDate: Moment = null
    public startDate: Moment = null
    public period: PeriodType = null
    public isLoading = false
    _rangePresets: Record<string, PeriodType> = getDefaultRangePresets()

    public clear() {
        this.startDate = null
        this.endDate = null
    }

    public setIsLoading = (isLoading: boolean) => {
        this.isLoading = isLoading
    }

    public setDates = ([startDate, endDate]): void => {
        this.startDate = startDate
        this.endDate = endDate

        this.period = getPeriod(startDate, endDate)
        this.setPeriodTitle()
    }

    public setPeriod = (period: PeriodType) => {
        const {startDate, endDate} = getDates(period)
        this.startDate = startDate
        this.endDate = endDate
        this.period = period
        this.setPeriodTitle()
    }

    public setPeriodTitle = () => {
        if (!this.period) {
            this.periodTitle = i18next.t('RangePicker.calendarTitle')
            return
        }

        switch (this.period) {
            case PeriodType.Custom:
                this.periodTitle = i18next.t('RangePicker.calendarTitle')
                break
            case PeriodType.Day:
                this.periodTitle = i18next.t('RangePicker.presets.today')
                break
            case PeriodType.Yesterday:
                this.periodTitle = i18next.t('RangePicker.presets.yesterday')
                break
            case PeriodType.Week:
                this.periodTitle = i18next.t('RangePicker.presets.thisWeek')
                break
            case PeriodType.Last7Days:
                this.periodTitle = i18next.t('RangePicker.presets.last7Days')
                break
            case PeriodType.LastWeek:
                this.periodTitle = i18next.t('RangePicker.presets.lastWeek')
                break
            case PeriodType.Month:
                this.periodTitle = i18next.t('RangePicker.presets.thisMonth')
                break
            case PeriodType.LastMonth:
                this.periodTitle = i18next.t('RangePicker.presets.lastMonth')
                break
            case PeriodType.Last30Days:
                this.periodTitle = i18next.t('RangePicker.presets.last30Days')
                break
            case PeriodType.Last60Days:
                this.periodTitle = i18next.t('RangePicker.presets.last60Days')
                break
            case PeriodType.Last90Days:
                this.periodTitle = i18next.t('RangePicker.presets.last90Days')
                break
            case PeriodType.Year:
                this.periodTitle = i18next.t('RangePicker.presets.thisYear')
                break
            case PeriodType.Last12Months:
                this.periodTitle = i18next.t('RangePicker.presets.last12Month')
                break
        }
    }

    public get rangePresets(): Record<string, PeriodType> {
        return this._rangePresets
    }

    get prevDates() {
        return getPrevDates(this.startDate, this.endDate, this.period)
    }
}
